import React from 'react';

export function useAccessGroupAction(
  refresh: () => Promise<unknown>,
  onSaved?: (message: string) => void,
): {
  busy: boolean;
  disabled: boolean;
  needsRefresh: boolean;
  error: string | null;
  message: string | null;
  retryRefresh: () => Promise<void>;
  execute: (
    mutation: () => Promise<unknown>,
    success: string,
  ) => Promise<boolean>;
} {
  const locked = React.useRef(false);
  const stale = React.useRef(false);
  const [busy, setBusy] = React.useState(false);
  const [needsRefresh, setNeedsRefresh] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [message, setMessage] = React.useState<string | null>(null);
  const reload = async (): Promise<boolean> => {
    try {
      await refresh();
      stale.current = false;
      setNeedsRefresh(false);
      return true;
    } catch {
      stale.current = true;
      setNeedsRefresh(true);
      return false;
    }
  };
  const retryRefresh = async (): Promise<void> => {
    if (locked.current) return;
    locked.current = true;
    setBusy(true);
    try {
      if (await reload()) setError(null);
    } finally {
      locked.current = false;
      setBusy(false);
    }
  };
  const execute = async (
    mutation: () => Promise<unknown>,
    success: string,
  ): Promise<boolean> => {
    if (locked.current || stale.current) return false;
    locked.current = true;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      let saved = false;
      let failureMessage: string | null = null;
      try {
        await mutation();
        saved = true;
      } catch (failure: unknown) {
        failureMessage =
          failure instanceof Error
            ? failure.message
            : 'Unable to confirm the change.';
      }
      if (saved) {
        setMessage(success);
        onSaved?.(success);
      }
      // A failed response can follow a committed mutation. Always read current
      // state before allowing another write, including after transport errors.
      if (!(await reload())) {
        setError(
          saved
            ? 'Saved in Odin, but the updated information could not be loaded. Reload data before making another change.'
            : `${failureMessage} The change may have been saved. Reload data before making another change.`,
        );
        return saved;
      }
      setError(failureMessage);
      return saved;
    } finally {
      locked.current = false;
      setBusy(false);
    }
  };
  return {
    busy,
    disabled: busy || needsRefresh,
    needsRefresh,
    error,
    message,
    execute,
    retryRefresh,
  };
}
